import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getUsers,
  getBranchUsers,
  getBranches,
  getRoles,
  deleteUser,
} from "../api";
import UserModal from "../components/UserModal";
import UserDetailsModal from "../components/UserDetailsModal";
import { FiPlus, FiEdit, FiTrash2 } from "react-icons/fi";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasAnyPermission, hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";
import useConfirmDialog from '../hooks/useConfirmDialog';

const BranchUsersPage = ({ branchId: branchIdProp, embedded = false, globalView = false }) => {
  const { branchId: paramBranchId } = useParams();
  const isGlobalView = Boolean(globalView);
  const resolvedBranchId = isGlobalView
    ? null
    : branchIdProp || paramBranchId || localStorage.getItem("selectedBranchId");
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branchUsers, setBranchUsers] = useState([]);
  const [branchAccessMap, setBranchAccessMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const pageSize = embedded ? 10 : 12;

  const canCreateUser = hasPermission(user, "user.create");
  const canUpdateUser = hasPermission(user, "user.update");
  const canDeleteUser = hasPermission(user, "user.delete");
  const canManagePermissions = hasPermission(user, "user.permissions.manage");
  const canManageRegisters = hasAnyPermission(user, [
    "cash_register.assign_users",
    "cash_register.manage",
  ]);
  const canEditUser = canUpdateUser || canManagePermissions || canManageRegisters;

  const branchUserIds = useMemo(
    () => new Set(branchUsers.map((entry) => String(entry.userId))),
    [branchUsers]
  );
  const selectedBranch = useMemo(
    () => branches.find((branch) => String(branch.id) === String(resolvedBranchId)),
    [branches, resolvedBranchId]
  );

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const normalizedSearch = search.trim();
      const [all, branchList, roleList] = await Promise.all([
        getUsers({ page, pageSize, ...(normalizedSearch ? { search: normalizedSearch } : {}) }),
        getBranches({ pageSize: 0 }),
        getRoles({ pageSize: 0 }),
      ]);
      const normalizedUsers = normalizePaginatedResponse(all);
      const normalizedBranches = normalizePaginatedResponse(branchList);
      setUsers(normalizedUsers.data || []);
      setTotalUsers(normalizedUsers.total || 0);
      setTotalPages(
        normalizedUsers.totalPages || Math.max(1, Math.ceil((normalizedUsers.total || 0) / pageSize))
      );
      setBranches(normalizedBranches.data || []);
      const normalizedRoles = normalizePaginatedResponse(roleList);
      setRoles(normalizedRoles.data || []);
      if (isGlobalView) {
        const branchUsersList = await Promise.all(
          (normalizedBranches.data || []).map((branch) =>
            getBranchUsers(branch.id, { pageSize: 0 })
              .then((data) => ({
                branchId: branch.id,
                data: normalizePaginatedResponse(data).data || [],
              }))
              .catch(() => ({ branchId: branch.id, data: [] }))
          )
        );
        const accessMap = {};
        branchUsersList.forEach(({ branchId, data }) => {
          (data || []).forEach((entry) => {
            const key = String(entry.userId);
            if (!accessMap[key]) {
              accessMap[key] = new Set();
            }
            accessMap[key].add(String(branchId));
          });
        });
        setBranchAccessMap(accessMap);
        setBranchUsers([]);
      } else {
        const branch = resolvedBranchId ? await getBranchUsers(resolvedBranchId, { pageSize: 0 }) : [];
        const normalizedBranchUsers = normalizePaginatedResponse(branch);
        setBranchUsers(normalizedBranchUsers.data || []);
        setBranchAccessMap({});
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("No se pudo cargar la informacion de usuarios.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [resolvedBranchId, isGlobalView, page, search]);

  useEffect(() => {
    setPage(1);
  }, [search, resolvedBranchId, isGlobalView]);

  const handleDelete = async (id) => {
    if (!canDeleteUser) return;
    const approved = await confirm({
      title: 'Eliminar usuario',
      message: '¿Estas seguro de eliminar este usuario?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    await deleteUser(id);
    fetchData();
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleOpenEdit = (selectedUser) => {
    setEditingUser(selectedUser);
    setShowModal(true);
  };

  const handleOpenDetails = (selectedUser) => {
    setSelectedUser(selectedUser);
  };

  const currentPage = Math.min(page, totalPages);
  const startIndex = totalUsers === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex = totalUsers === 0 ? 0 : Math.min(currentPage * pageSize, totalUsers);
  const pagedUsers = users;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const resolveAccessSummary = (item) => {
    const rolesForUser = item.Roles || [];
    const adminRole = rolesForUser.find(
      (role) =>
        role.name === "Admin" &&
        (role.UserRole?.branchId === null || role.UserRole?.branchId === undefined)
    );
    if (isGlobalView) {
      const accessSet = branchAccessMap[String(item.id)] || new Set();
      const branchRoleCount = rolesForUser.filter(
        (role) => role.UserRole?.branchId !== null && role.UserRole?.branchId !== undefined
      ).length;
      const accessLabel = adminRole
        ? "Acceso total"
        : accessSet.size > 0
          ? `Acceso en ${accessSet.size} sucursales`
          : "Sin acceso";
      const roleLabel = adminRole
        ? "Admin (global)"
        : branchRoleCount > 0
          ? `Roles en ${branchRoleCount} sucursales`
          : "Sin rol";
      const accessTone = adminRole ? "admin" : accessSet.size > 0 ? "scoped" : "none";
      return {
        adminRole,
        accessLabel,
        roleLabel,
        accessTone,
      };
    }
    const branchRole = rolesForUser.find(
      (role) => String(role.UserRole?.branchId) === String(resolvedBranchId)
    );
    const hasBranchAccess = Boolean(adminRole) || branchUserIds.has(String(item.id));
    const accessLabel = adminRole
      ? "Acceso total"
      : hasBranchAccess
        ? "Acceso en sucursal"
        : "Sin acceso";
    const roleLabel = adminRole
      ? "Admin (global)"
      : branchRole?.name || "Sin rol";
    const accessTone = adminRole ? "admin" : hasBranchAccess ? "scoped" : "none";
    return {
      adminRole,
      accessLabel,
      roleLabel,
      accessTone,
    };
  };

  if (loading) {
    return (
      <p className={embedded ? "text-sm text-gray-500" : "p-4"}>
        <InlineLoading />
      </p>
    );
  }

  const branchLabel = isGlobalView
    ? "Configuración de usuarios"
    : selectedBranch?.branchName
      ? `Sucursal: ${selectedBranch.branchName}`
      : "Sucursal seleccionada";

  return (
    <div className={embedded ? "space-y-4" : "p-6 max-w-6xl mx-auto"}>
      <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${embedded ? "" : "mb-6"}`}>
        <div>
          <h1 className={embedded ? "text-lg font-semibold" : "text-2xl font-bold"}>
            Usuarios
          </h1>
          <p className="text-sm text-gray-500">{branchLabel}</p>
        </div>
        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o email"
            className="border px-3 py-2 rounded w-full md:w-64"
          />
          {canCreateUser && (
            <button
              onClick={handleOpenCreate}
              className="bg-blue-500 text-white px-4 py-2 rounded flex items-center justify-center gap-2"
            >
              <FiPlus /> Usuario
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600">{error}</div>
      )}

      <div className="bg-white rounded shadow divide-y">
        {users.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">No hay usuarios para mostrar.</p>
        ) : (
          pagedUsers.map((item) => {
            const { accessLabel, roleLabel, accessTone } = resolveAccessSummary(item);
            return (
              <div
                key={item.id}
                className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 cursor-pointer hover:bg-gray-50 transition"
                onClick={() => handleOpenDetails(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleOpenDetails(item);
                  }
                }}
              >
                <div>
                  <p className="font-medium">{item.name || item.username}</p>
                  <p className="text-sm text-gray-500">{item.username}</p>
                </div>
                <div className="flex flex-wrap gap-2 text-xs font-medium">
                  <span
                    className={`px-2 py-1 rounded ${
                      accessTone === "admin"
                        ? "bg-indigo-100 text-indigo-700"
                        : accessTone === "scoped"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {accessLabel}
                  </span>
                  <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">
                    {roleLabel}
                  </span>
                </div>
                <div className="flex gap-2">
                  {canEditUser && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit(item);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="bg-blue-500 text-white px-3 py-2 rounded-lg"
                      title="Editar"
                    >
                      <FiEdit />
                    </button>
                  )}
                  {canDeleteUser && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="bg-red-500 text-white px-3 py-2 rounded-lg"
                      title="Eliminar"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {totalUsers > pageSize && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-4">
          <div>
            Mostrando {startIndex}-{endIndex} de {totalUsers}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Anterior
            </button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <UserModal
          user={editingUser}
          branches={branches}
          roles={roles}
          canManagePermissions={canManagePermissions}
          canManageRegisters={canManageRegisters}
          canUpdateUser={canUpdateUser}
          canCreateUser={canCreateUser}
          onSaved={fetchData}
          onClose={() => {
            setShowModal(false);
            setEditingUser(null);
          }}
        />
      )}

      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          branches={branches}
          branchUsers={branchUsers}
          branchAccessMap={branchAccessMap}
          isGlobalView={isGlobalView}
          resolvedBranchId={resolvedBranchId}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

export default BranchUsersPage;
